import { Component, OnInit } from '@angular/core';
import { BankPaymentsService } from 'src/app/services/misc/bank-payments.service';
import { GeneralService } from 'src/app/services/general.service';
import { BankService } from 'src/app/services/settings-services/bank.service';
import { forkJoin, BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import PaymentFilter from './payment-filters';
import { BsDatepickerViewMode } from 'ngx-bootstrap/datepicker/models';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { timestamp } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';

@Component({
  selector: 'app-bank-payments',
  templateUrl: './bank-payments.component.html',
  styleUrls: ['./bank-payments.component.css']
})
export class BankPaymentsComponent implements OnInit {
  /** LOADER */
  loading: boolean = false;

  /** */
  current_account_id: string = '';
  selected_account_id: string = '';
  bank_accounts: Array<any> = [];
  payment_records: any = [];
  account_currency: string = '';
  receipt_substring: string = 'Receipt payment of';

  /** Filter handler */
  filter: any;

  /** DATE PICKER */
  minMode: BsDatepickerViewMode = 'month';
  bsConfig: Partial<BsDatepickerConfig> = { adaptivePosition: true };

  /** DATATABLE CONFIG */
  dataTable = {
    dataChangedObs: new BehaviorSubject(null),

    heads: [
      { title: "checkbox", key: "checkbox" },
      // { title: "ID", key: "id" },
      { title: "Client", key: "clientName" },
      { title: "Invoice/Receipt Number", key: "invoiceId" },
      { title: "Description", key: "description" },
      { title: "Amount Paid", key: "amountPaid", transform: (amount, rowData) => this.general_service.format_as_money(amount) },
      { title: "Date", key: "createdOn", transform: (timestamp, rowData) => (new Date(timestamp)).toDateString() },
      { title: "Action", key: "action" }
    ],
    options: {
      bulkActions: ['Delete'],
      singleActions: [
        {
          title: "Payment Info",
          showIf: () => this.general_service.isAuthorized("VIEW_PAYMENT_INFO"), 
        },
        {
          title: "Delete",
          showIf: () => this.general_service.isAuthorized("DELETE_PAYMENT"),
        }],
    }
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bank_service: BankService,
    private bank_payment_service: BankPaymentsService,
    public general_service: GeneralService,
    private toast: ToastrService,
    private _location: Location,
  ) { }

  ngOnInit() {

    this.route.params.subscribe(param => {
      if (this.general_service.checkIfObjectIsEmpty(param)) return;
      const { id } = param;
      this.filter = new PaymentFilter();
      this.current_account_id = id;
      this.selected_account_id = id;
      this.get_bank_accounts();
      this.get_account_payment_records(id);
    });
  }

  get_bank_accounts() {
    this.bank_service.fetch_bank_records().subscribe((response: any) => {
      if (response.success) {
        this.bank_accounts = response.payload;
        response.payload.forEach(account => {
          if (account.id == this.selected_account_id) {
            this.account_currency = account.currency;
          }
        });
      }
    }, err => {
    });
  }


  /**
   * Get payments for the given bank account
   * 
   * @param id {AccountId}
   */
   get_account_payment_records(id) {
    this.loading = true;
    this.bank_payment_service.filter_organisation_payments(`accountId=${id}`).subscribe((response: any) => {
      this.loading = false;
      if (response.success) {
        let payments = response.payload;
        payments = payments.filter(pay => !pay.fromReceipt)
        this.payment_records = payments.map(payment => ({
          ...payment,
          clientName: payment.client.name,
          clientId: payment.client.id,
          invoiceId: (payment.description && payment.description.includes(this.receipt_substring)? this.append_id_stub('REC-', payment.invoiceId) : this.append_id_stub('INV-', payment.invoiceId))
        }));
        this.dataTable.dataChangedObs.next(true);
      }
    }, err => {
      this.loading = false;
    });
  }

  /**
   * Navigate to previous location
   */
  goBack() {
    this._location.back();
  }

  /**
   * Refresh payments for the specified account.
   * 
   * @param id the account id for which to load payments
   */

  refresh_payments(id) {
    this.payment_records = [];
    this.account_currency = this.bank_accounts.reduce((currency, account) => {
      if (id === account.id) return account.currency;
    }, '');

    this.get_account_payment_records(id);
  }

  /** 
   * Formats numeric ids into standard display form.
   * 
   * @param stub {string} 
   * @param id {number}
   * 
   * @returns a formatted id string
   */

  append_id_stub(stub: string, id: number) {
    let paddingCount = 7 - (id).toString().length;
    let finalId = ``;

    for (let i = 0; i < paddingCount; i++) {
      finalId += `0`;
    }

    return `${stub}${finalId}${id}`;
  }

  apply_filter() {
    const query = `accountId=${this.selected_account_id}&${this.filter.build_query_string()}`;
    this.bank_payment_service.filter_organisation_payments(query).subscribe((response: any) => {
      if (response.success) {
        this.payment_records = response.payload.map(payment => ({
          ...payment,
          clientName: payment.client.name,
          clientId: payment.client.id,
          invoiceId: (payment.description && payment.description.includes(this.receipt_substring)? this.append_id_stub('REC-', payment.invoiceId): this.append_id_stub('INV-', payment.invoiceId))
        }));
        this.dataTable.dataChangedObs.next(true);
      }
    }, error => this.general_service.sweetAlertError(error.message));
  }

  clear_filters() {
    this.filter.clear_filters();
    this.refresh_payments(this.selected_account_id);
  }

  /**
   * Make selections on simple key => value filter keys
   * 
   * @param filter_field The filter key
   * @param value   The provided value
   */
  make_single_selection(filter_field: string, value: any) {
    this.filter.make_single_selection(filter_field, value);
    this.apply_filter();
  }

  /**
   * Make a range selection
   * 
   * @param filter_field The field to filter by
   * @param value  {number} The value to set
   * @param is_min {boolean} Set to true if it is minimum range value. False if it is the maximum range value
   */
  make_range_selection(filter_field: string, value: number, is_min = true) {
    this.filter.make_range_selection(filter_field, Number(value), is_min);
    this.apply_filter();
  }

  datatable_event_handler = data => {
    if (data.type == 'singleaction') {
      switch (data.action) {
        case 'Payment Info':
          if(data.data.description && data.data.description.includes(this.receipt_substring)){
            console.log('data here', data.data);
            const receiptId = data.data.invoiceId;
            const split = receiptId.split('-');
            if(split.length > 0){
              const id = split[1];
              let receiptCode = 'REC-'+ data.data.orgId + '-'+id;
              this.router.navigate(["/sales/edit-receipt/" + receiptCode]);
         
            }
            
          }else
            this.router.navigate(["/sales/edit-invoice/" + data.data.invoiceId]);
          break;
        case 'Delete':
          this.general_service.sweetAlertGeneralDelete('Delete Payment', 'Continue').then(proceed => {
            if (proceed && proceed.value) {
              this.bank_payment_service.delete_payment(data.data.id).subscribe(response => {
                if (response.success) {
                  this.toast.success(`Payment ${data.data.id} deleted successfully.`);
                  this.refresh_payments(this.current_account_id);
                }
              });
            }
          });
          break;
      }
    }

    if (data.type.toLowerCase() == 'bulkactions') {
      switch (data.action) {
        case 'Delete':
          this.general_service.sweetAlertGeneralDelete('Delete Payments', 'Continue').then(proceed => {
            if (proceed) {
              const ids = data.data.map(item => item.id);
              this.bank_payment_service.delete_payment(ids.join()).subscribe(response => {
                if (response.success) {
                  this.toast.success(`Payments deleted successfully.`);
                  this.refresh_payments(this.current_account_id);
                }
              });
            }
          });
          break;
      }
    }
  };
}
